import { Link, useParams } from "react-router-dom";
import { motion, useScroll } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { ARTICLES, EMBLEM_URL } from "@/data/content";

const Block = ({ block, isFirst }) => {
  if (block.type === "h2") {
    return (
      <h2 className="mt-16 flex items-baseline gap-4 font-display text-3xl font-bold uppercase tracking-wide text-[#F4F5F7] sm:text-4xl">
        <span className="h-px w-8 shrink-0 self-center bg-[#E52E2E]" />
        {block.text}
      </h2>
    );
  }
  if (block.type === "quote") {
    return (
      <blockquote className="relative my-14 py-2 pl-8 sm:pl-12">
        <span className="absolute -top-4 left-0 select-none font-display text-7xl font-black leading-none text-[#E52E2E]">
          "
        </span>
        <p className="font-display text-2xl font-semibold uppercase leading-snug tracking-wide text-[#F4F5F7] sm:text-3xl">
          {block.text}
        </p>
        <span className="mt-4 block h-px w-16 bg-[#E52E2E]" />
      </blockquote>
    );
  }
  return (
    <p
      className={`mt-7 text-base leading-[1.85] text-[#9EA4B0] sm:text-lg ${
        isFirst
          ? "first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:font-display first-letter:text-6xl first-letter:font-black first-letter:leading-[0.85] first-letter:text-[#E52E2E]"
          : ""
      }`}
    >
      {block.text}
    </p>
  );
};

const RelatedRow = ({ article, index }) => (
  <Link
    to={`/journal/${article.slug}`}
    data-testid={`related-${article.slug}`}
    className="group relative flex items-center gap-6 border-b border-[#282D37] px-2 py-7 transition-colors duration-500 hover:bg-[#121418] sm:gap-8 sm:px-6"
  >
    <span className="hidden font-mono text-sm tracking-[0.3em] text-[#3A404E] transition-colors duration-500 group-hover:text-[#E52E2E] sm:block">
      {String(index + 1).padStart(2, "0")}
    </span>
    <div className="min-w-0 flex-1">
      <p className="font-mono text-[10px] tracking-[0.3em] text-[#E52E2E]">{article.category}</p>
      <h3 className="mt-2 font-display text-xl font-bold uppercase leading-tight tracking-wide text-[#F4F5F7] transition-colors duration-300 group-hover:text-[#E52E2E] sm:text-2xl">
        {article.title}
      </h3>
    </div>
    <div className="relative hidden h-16 w-28 shrink-0 overflow-hidden border border-[#282D37] md:block">
      <img
        src={article.image}
        alt=""
        className="h-full w-full object-cover opacity-80 saturate-[0.85] transition-[transform,opacity] duration-700 group-hover:scale-105 group-hover:opacity-100"
      />
    </div>
    <ArrowUpRight
      size={20}
      className="shrink-0 text-[#3A404E] transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#E52E2E]"
    />
    <span className="absolute bottom-0 left-0 h-px w-0 bg-[#E52E2E] transition-[width] duration-700 group-hover:w-full" />
  </Link>
);

const Article = () => {
  const { slug } = useParams();
  const { scrollYProgress } = useScroll();
  const article = ARTICLES.find((a) => a.slug === slug);
  const related = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);

  if (!article) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center px-6 pt-32 text-center">
        <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">SIGNAL LOST</p>
        <h1 className="mt-4 font-display text-4xl font-black uppercase text-[#F4F5F7]">Dispatch not found</h1>
        <Link
          to="/journal"
          data-testid="article-notfound-back"
          className="mt-8 border border-[#3A404E] px-8 py-4 font-mono text-xs font-bold tracking-[0.25em] text-[#F4F5F7] transition-colors duration-300 hover:border-[#E52E2E] hover:text-[#E52E2E]"
        >
          RETURN TO JOURNAL
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-24">
      <motion.div
        data-testid="reading-progress"
        className="fixed inset-x-0 top-0 z-[60] h-[2px] origin-left bg-[#E52E2E]"
        style={{ scaleX: scrollYProgress }}
      />

      <header className="mx-auto max-w-4xl px-6 pt-14">
        <Reveal>
          <Link
            to="/journal"
            data-testid="article-back-link"
            className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] text-[#9EA4B0] transition-colors duration-300 hover:text-[#E52E2E]"
          >
            <ArrowLeft size={14} /> ALL DISPATCHES
          </Link>
          <div className="mt-12 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[11px] tracking-[0.3em]">
            <span className="bg-[#E52E2E] px-3 py-1 font-bold text-white">{article.category}</span>
            <span className="text-[#5F6573]">{article.date}</span>
            <span className="text-[#5F6573]">·</span>
            <span className="text-[#5F6573]">{article.readTime} READ</span>
          </div>
          <h1 className="mt-8 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight text-[#F4F5F7] sm:text-6xl lg:text-7xl">
            {article.title}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#9EA4B0] sm:text-lg">{article.summary}</p>
          <div className="mt-10 flex items-center justify-between border-y border-[#282D37] py-5">
            <div className="flex items-center gap-3">
              <img src={EMBLEM_URL} alt="Arsenal Forge emblem" className="w-9" />
              <div>
                <p className="font-mono text-xs font-bold tracking-[0.2em] text-[#F4F5F7]">ARSENAL FORGE</p>
                <p className="font-mono text-[10px] tracking-[0.25em] text-[#5F6573]">STUDIO TRANSMISSION</p>
              </div>
            </div>
            <p className="hidden font-mono text-[10px] tracking-[0.3em] text-[#5F6573] sm:block">
              DISPATCH // {article.slug.toUpperCase().replace(/-/g, ".")}
            </p>
          </div>
        </Reveal>
      </header>

      <Reveal delay={0.1}>
        <figure className="mx-auto mt-12 max-w-5xl px-6">
          <div className="overflow-hidden border border-[#282D37]">
            <img
              src={article.image}
              alt={article.title}
              className="aspect-[16/8] w-full object-cover opacity-95 saturate-[0.85] contrast-[1.05]"
            />
          </div>
          <figcaption className="mt-3 text-right font-mono text-[10px] tracking-[0.3em] text-[#5F6573]">
            FIG. 01 — {article.category}
          </figcaption>
        </figure>
      </Reveal>

      <article data-testid="article-body" className="mx-auto max-w-3xl px-6 pb-8 pt-14">
        {article.body.map((block, i) => (
          <Reveal key={i} delay={0.02 * i} y={20}>
            <Block block={block} isFirst={i === 0} />
          </Reveal>
        ))}
        <Reveal>
          <div className="mt-16 flex items-center justify-center gap-4">
            <span className="h-px w-16 bg-[#282D37]" />
            <span className="h-2 w-2 rotate-45 bg-[#E52E2E]" />
            <span className="h-px w-16 bg-[#282D37]" />
          </div>
          <p className="mt-6 text-center font-mono text-[10px] tracking-[0.35em] text-[#5F6573]">
            END OF TRANSMISSION
          </p>
        </Reveal>
      </article>

      <section className="border-t border-[#282D37] bg-[#0D0F12]">
        <div className="mx-auto max-w-4xl px-6 py-20">
          <Reveal>
            <div className="flex items-end justify-between gap-6">
              <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">KEEP READING</p>
              <Link
                to="/journal"
                data-testid="article-all-dispatches"
                className="font-mono text-[11px] font-bold tracking-[0.25em] text-[#9EA4B0] transition-colors duration-300 hover:text-[#E52E2E]"
              >
                ALL DISPATCHES
              </Link>
            </div>
          </Reveal>
          <div className="mt-8 border-t border-[#282D37]">
            {related.map((a, i) => (
              <Reveal key={a.slug} delay={0.06 * i}>
                <RelatedRow article={a} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Article;
